import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function MeuPerfil() {
  const { user, ehFornecedor, aplicarToken } = useAuth();
  const navigate = useNavigate();
  const provisoria = ehFornecedor && user?.senha_provisoria;

  const [f, setF] = useState({ atual: '', nova: '', confirma: '' });
  const [email, setEmail] = useState('');
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [emailErro, setEmailErro] = useState('');
  const [emailOk, setEmailOk] = useState(false);
  const [emailSalvando, setEmailSalvando] = useState(false);

  // Carrega o e-mail atual (pra pré-preencher o campo)
  useEffect(() => {
    if (!ehFornecedor) return;
    api.get('/perfil').then(({ data }) => setEmail(data.email || '')).catch(() => {});
  }, [ehFornecedor]);

  // Primeiro acesso: e-mail + nova senha num passo só
  const submitPrimeiroAcesso = async (e) => {
    e.preventDefault();
    setErro(''); setOk(false);
    if (!EMAIL_RE.test(email.trim())) return setErro('Informe um e-mail válido — é por onde você recupera a senha.');
    if (f.nova.length < 6) return setErro('A nova senha precisa ter ao menos 6 caracteres.');
    if (f.nova !== f.confirma) return setErro('A confirmação não bate com a nova senha.');
    setSalvando(true);
    try {
      const { data } = await api.post('/perfil/trocar-senha', { senha_atual: f.atual, senha_nova: f.nova, email: email.trim() });
      if (data.token) aplicarToken(data.token);
      setOk(true);
      setTimeout(() => navigate('/'), 900);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao concluir o primeiro acesso.');
    } finally { setSalvando(false); }
  };

  // Troca de senha (uso normal)
  const submitSenha = async (e) => {
    e.preventDefault();
    setErro(''); setOk(false);
    if (f.nova.length < 6) return setErro('A nova senha precisa ter ao menos 6 caracteres.');
    if (f.nova !== f.confirma) return setErro('A confirmação não bate com a nova senha.');
    setSalvando(true);
    try {
      const { data } = await api.post('/perfil/trocar-senha', { senha_atual: f.atual, senha_nova: f.nova });
      if (data.token) aplicarToken(data.token);
      setOk(true);
      setF({ atual: '', nova: '', confirma: '' });
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao trocar a senha.');
    } finally { setSalvando(false); }
  };

  // Salvar/atualizar e-mail (uso normal)
  const salvarEmail = async (e) => {
    e.preventDefault();
    setEmailErro(''); setEmailOk(false);
    if (!EMAIL_RE.test(email.trim())) return setEmailErro('Informe um e-mail válido.');
    setEmailSalvando(true);
    try {
      await api.post('/perfil/email', { email: email.trim() });
      setEmailOk(true);
    } catch (err) {
      setEmailErro(err.response?.data?.error || 'Erro ao salvar o e-mail.');
    } finally { setEmailSalvando(false); }
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <h4 className="fw-bold mb-4">
        <i className="bi bi-person-gear me-2" style={{ color: 'var(--primary)' }} />
        Meu Perfil
      </h4>

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <i className="bi bi-person-circle" style={{ fontSize: '2.4rem', color: 'var(--primary)' }} />
          <div>
            <div style={{ fontWeight: 700 }}>{user?.nome}</div>
            <div style={{ color: 'var(--muted)', fontSize: '.88rem' }}>
              {ehFornecedor ? `Fornecedor · ${user?.fornecedor_nome || ''}` : `Administrador · ${user?.username}`}
            </div>
          </div>
        </div>
      </div>

      {!ehFornecedor ? (
        <div className="card" style={{ padding: 20, color: 'var(--muted)' }}>
          A senha do administrador é a mesma do seu login do Centrão — a troca é feita pelo sistema interno.
        </div>
      ) : provisoria ? (
        // ---- PRIMEIRO ACESSO: e-mail (obrigatório) + nova senha ----
        <>
          <div className="card" style={{ padding: 14, marginBottom: 18, borderLeft: '4px solid var(--accent)', background: 'var(--accent-soft)' }}>
            <b><i className="bi bi-shield-lock" /> Bem-vindo! Conclua seu primeiro acesso</b>
            <div style={{ fontSize: '.88rem', color: 'var(--text-suave)' }}>
              Confirme seu <b>e-mail</b> (usado pra recuperar a senha) e crie uma <b>senha pessoal</b> para continuar.
            </div>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <form onSubmit={submitPrimeiroAcesso} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: '.88rem', marginBottom: 4, display: 'block' }}>Seu e-mail</label>
                <input className="form-control" type="email" placeholder="voce@empresa.com.br" required
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <hr style={{ margin: '6px 0', borderColor: 'var(--borda)' }} />
              <input className="form-control" type="password" placeholder="Senha provisória (a que você recebeu)" required
                value={f.atual} onChange={(e) => setF({ ...f, atual: e.target.value })} />
              <input className="form-control" type="password" placeholder="Nova senha (mín. 6)" required
                value={f.nova} onChange={(e) => setF({ ...f, nova: e.target.value })} />
              <input className="form-control" type="password" placeholder="Confirmar nova senha" required
                value={f.confirma} onChange={(e) => setF({ ...f, confirma: e.target.value })} />
              {erro && <div className="for-alert"><i className="bi bi-exclamation-triangle" /> {erro}</div>}
              {ok && <div className="for-ok"><i className="bi bi-check-circle" /> Tudo certo! Entrando...</div>}
              <button className="btn-primary-for" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar e continuar'}</button>
            </form>
          </div>
        </>
      ) : (
        // ---- USO NORMAL: e-mail e senha em blocos separados ----
        <>
          <div className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>E-mail de contato</div>
            <div style={{ fontSize: '.85rem', color: 'var(--text-suave)', marginBottom: 12 }}>
              Usado pra recuperar sua senha. Mantenha atualizado.
            </div>
            <form onSubmit={salvarEmail} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input className="form-control" type="email" placeholder="voce@empresa.com.br" required
                value={email} onChange={(e) => { setEmail(e.target.value); setEmailOk(false); setEmailErro(''); }} />
              {emailErro && <div className="for-alert"><i className="bi bi-exclamation-triangle" /> {emailErro}</div>}
              {emailOk && <div className="for-ok"><i className="bi bi-check-circle" /> E-mail salvo!</div>}
              <button className="btn-primary-for" disabled={emailSalvando}>{emailSalvando ? 'Salvando...' : 'Salvar e-mail'}</button>
            </form>
          </div>
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>Trocar senha</div>
            <form onSubmit={submitSenha} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input className="form-control" type="password" placeholder="Senha atual" required
                value={f.atual} onChange={(e) => setF({ ...f, atual: e.target.value })} />
              <input className="form-control" type="password" placeholder="Nova senha (mín. 6)" required
                value={f.nova} onChange={(e) => setF({ ...f, nova: e.target.value })} />
              <input className="form-control" type="password" placeholder="Confirmar nova senha" required
                value={f.confirma} onChange={(e) => setF({ ...f, confirma: e.target.value })} />
              {erro && <div className="for-alert"><i className="bi bi-exclamation-triangle" /> {erro}</div>}
              {ok && <div className="for-ok"><i className="bi bi-check-circle" /> Senha atualizada!</div>}
              <button className="btn-primary-for" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar nova senha'}</button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
